import React, { useState, useEffect, useCallback } from "react";
import { X, ImageIcon, Download, Loader2, Sparkles, AlertCircle, Star, ChevronLeft, ChevronRight, ThumbsDown, RefreshCw } from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

/**
 * PhotoSearchModal — modale che cerca foto prodotto AI-ranked dal sito del
 * fornitore (Brave Search) e le filtra con Gemini Vision per mostrare le 3
 * più "pack-shot" (prodotto in primo piano, no troppa ambientazione).
 *
 * Props:
 *  - open: bool — visibilità modale
 *  - onClose: () => void
 *  - productName: string — testo della query AI (es: query utente)
 *  - supplierId: string — UUID del fornitore (per scopare al suo sito)
 *  - supplierName: string — solo per UI display
 *  - color: string — opzionale (filtro colore)
 *
 * Flow:
 *  - Apertura → POST /api/preventivatore/photo-search → loader 5-15s
 *  - Risposta → 3 miniature in griglia
 *  - Click miniatura → vista ingrandita con bottone Download (via proxy server-side)
 *  - Frecce ← → per navigare nella vista ingrandita
 */
export default function PhotoSearchModal({ open, onClose, productName, supplierId, supplierName, color, initialIdx = -1, initialResults = null, canReject = true }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [cached, setCached] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(initialIdx); // -1 = griglia, >=0 = ingrandita
  const [poolSize, setPoolSize] = useState(0); // candidati disponibili (visibili)
  const [poolTotal, setPoolTotal] = useState(0); // candidati totali nel pool (visibili + scartati)
  const [rejectingUrl, setRejectingUrl] = useState(""); // URL in fase di scarto (loading UI)

  const search = useCallback(async (opts = {}) => {
    if (!productName?.trim()) return;
    setLoading(true);
    setError("");
    if (!opts.preserveItems) setItems([]);
    setCached(false);
    // NOTA: NON resettiamo selectedIdx qui — l'apertura del modal lo gestisce
    // via initialIdx. Reset solo se richiesto esplicitamente.
    if (opts.resetSelected) setSelectedIdx(-1);
    try {
      const { data } = await api.post("/preventivatore/photo-search", {
        product_name: productName.trim(),
        supplier_id: supplierId || "",
        color: color || "",
        force_refresh: !!opts.forceRefresh,
      });
      setItems(data.items || []);
      setCached(!!data.cached);
      setQuery(data.query || "");
      setPoolSize(Number(data.pool_size || 0));
      setPoolTotal(Number(data.pool_total || 0));
      if ((data.items || []).length === 0) {
        const debugReason = data?._debug?.reason;
        setError(debugReason || "Nessuna foto trovata per questo prodotto. Prova a riformulare la ricerca o aggiungi il nome del modello.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Errore ricerca foto";
      setError(msg);
      toast.error(`Ricerca foto: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [productName, supplierId, color]);

  // Scarta una foto: registra rejection backend → re-fetch (cache pool slide)
  const rejectPhoto = useCallback(async (url) => {
    if (!url) return;
    setRejectingUrl(url);
    try {
      await api.post("/preventivatore/photo-search/reject", {
        product_name: productName.trim(),
        supplier_id: supplierId || "",
        url,
        reason: "scartata da utente",
      });
      toast.success("Foto scartata · cerco un sostituto");
      // Torna alla griglia se era selezionata
      setSelectedIdx(-1);
      // Re-fetch (cache hit → ricalcola top filtrando le scarti)
      await search({ preserveItems: false });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Errore scarto";
      toast.error(`Scarto: ${msg}`);
    } finally {
      setRejectingUrl("");
    }
  }, [productName, supplierId, search]);

  // Cerca nuove foto: bypassa la cache e chiede a Brave nuovi candidati
  const refreshPool = useCallback(async () => {
    await search({ forceRefresh: true });
    toast.success("Pool aggiornato con nuove foto");
  }, [search]);

  useEffect(() => {
    if (open) {
      setSelectedIdx(initialIdx);
      // Se il chiamante ci passa già i risultati pre-caricati (vedi
      // Preventivatore top-photo-strip), li mostriamo immediatamente e
      // saltiamo il fetch iniziale. Le successive operazioni (reject,
      // refresh) richiameranno la search normalmente.
      if (initialResults && initialResults.length > 0) {
        setItems(initialResults);
        setLoading(false);
        setError("");
        return;
      }
      search();
    } else {
      // reset al chiusura
      setItems([]);
      setSelectedIdx(-1);
      setError("");
    }
  }, [open, search, initialIdx, initialResults]);

  // ESC chiude (o esce dalla vista ingrandita prima)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (selectedIdx >= 0) setSelectedIdx(-1);
        else onClose?.();
      } else if (selectedIdx >= 0 && e.key === "ArrowLeft") {
        setSelectedIdx((i) => Math.max(0, i - 1));
      } else if (selectedIdx >= 0 && e.key === "ArrowRight") {
        setSelectedIdx((i) => Math.min(items.length - 1, i + 1));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, selectedIdx, items.length, onClose]);

  function downloadPhoto(item) {
    // Apre il proxy server-side che forza Content-Disposition: attachment
    const proxyUrl = `${API}/preventivatore/photo-proxy?url=${encodeURIComponent(item.url)}`;
    // Aprire in una <a download> non funziona cross-origin, ma il backend
    // setta gli header giusti quindi window.open con _self triggera il download.
    const a = document.createElement("a");
    a.href = proxyUrl;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  }

  if (!open) return null;

  const selected = selectedIdx >= 0 ? items[selectedIdx] : null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-ink/70 backdrop-blur-sm"
      data-testid="photo-search-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (selectedIdx >= 0) setSelectedIdx(-1);
          else onClose?.();
        }
      }}
    >
      <div className="bg-cream w-[min(98vw,1100px)] max-h-[94vh] overflow-hidden flex flex-col border border-ink/15 shadow-2xl rounded-sm">
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink/10 flex items-start justify-between gap-3 bg-white">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-sm bg-gold/15 text-gold flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">
                Foto prodotto · AI ranked
              </div>
              <div className="text-base font-light text-ink mt-0.5 truncate" title={productName}>
                {productName || "—"}
              </div>
              <div className="text-[11px] text-slate1 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                {supplierName && <span>Fornitore: <strong className="text-ink">{supplierName}</strong></span>}
                {color && <span>· Colore: <strong className="text-ink">{color}</strong></span>}
                {cached && <span className="text-gold">· da cache</span>}
              </div>
            </div>
          </div>
          <button
            onClick={() => onClose?.()}
            className="text-slate1 hover:text-ink p-1 rounded-sm hover:bg-ink/5 transition-colors shrink-0"
            aria-label="Chiudi"
            data-testid="photo-search-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-cream/40">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="photo-search-loading">
              <Loader2 size={32} className="text-gold animate-spin" />
              <div className="text-sm text-slate1">Cerco su {supplierName ? `sito di ${supplierName}` : "web"} + ranking AI…</div>
              <div className="text-[11px] text-slate1/70">Richiede 5-15 secondi al primo tentativo</div>
            </div>
          )}

          {!loading && error && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <AlertCircle size={28} className="text-slate1" />
              <div className="text-sm text-slate1 max-w-lg text-center">{error}</div>
            </div>
          )}

          {/* Griglia 3 miniature */}
          {!loading && items.length > 0 && selectedIdx < 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="photo-search-grid">
                {items.map((it, i) => (
                  <div
                    key={`${it.url}-${i}`}
                    className="group relative bg-white border border-ink/10 rounded-sm overflow-hidden hover:border-gold transition-colors flex flex-col"
                    data-testid={`photo-search-thumb-${i}`}
                  >
                    {/* Thumbnail (click → ingrandita) */}
                    <button
                      type="button"
                      onClick={() => setSelectedIdx(i)}
                      className="relative aspect-[4/3] bg-cream2 overflow-hidden w-full text-left"
                      title="Apri in vista ingrandita"
                    >
                      <img
                        src={it.thumbnail}
                        alt={it.title || `Risultato ${i + 1}`}
                        className="w-full h-full object-contain transition-transform group-hover:scale-105"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          if (it.url && e.target.src !== it.url) {
                            e.target.src = it.url;
                          }
                        }}
                      />
                      {/* Rank badge */}
                      <div className="absolute top-2 left-2 bg-ink/80 text-cream rounded-sm px-2 py-1 text-[10px] tracking-[0.2em] uppercase backdrop-blur-sm">
                        #{i + 1}
                      </div>
                      {/* Score badge */}
                      <div className="absolute top-2 right-2 bg-gold/95 text-ink rounded-sm px-2 py-1 text-[10px] tracking-[0.15em] uppercase font-medium backdrop-blur-sm flex items-center gap-1">
                        <Star size={9} fill="currentColor" />
                        {Math.round(it.score)}
                      </div>
                    </button>
                    {/* Reject button overlay (in alto sopra la meta) */}
                    {canReject && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); rejectPhoto(it.url); }}
                      disabled={rejectingUrl === it.url}
                      className="absolute top-2 right-14 bg-rose-50/95 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-sm px-2 py-1 text-[10px] tracking-[0.15em] uppercase font-medium backdrop-blur-sm inline-flex items-center gap-1 transition-colors disabled:opacity-50"
                      title="Scarta questa foto e cercane un'altra"
                      data-testid={`photo-search-reject-${i}`}
                    >
                      {rejectingUrl === it.url ? <Loader2 size={10} className="animate-spin" /> : <ThumbsDown size={10} />}
                      Scarta
                    </button>
                    )}
                    {/* Meta */}
                    <div className="p-3 text-[11px] text-slate1 flex-1 flex flex-col">
                      <div className="text-ink text-xs font-medium line-clamp-2 leading-snug" title={it.title}>
                        {it.title || "Senza titolo"}
                      </div>
                      {it.reason && (
                        <div className="mt-1.5 text-[11px] italic text-slate1 line-clamp-2 leading-relaxed">
                          <span className="text-gold not-italic">↳</span> {it.reason}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[10px] tracking-[0.15em] uppercase text-slate1/70 flex items-center gap-3 flex-wrap">
                <Sparkles size={11} className="text-gold" />
                <span>Ranking: Gemini · furniture 30% + match 40% + clarity 20% + color 10%</span>
                {poolTotal > items.length && (
                  <span className="text-slate1">Pool: <span className="text-ink">{poolSize}</span> disponibili / {poolTotal} totali · {poolTotal - poolSize} scartate</span>
                )}
                <button
                  type="button"
                  onClick={refreshPool}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-gold/40 text-gold hover:bg-gold/10 rounded-sm transition-colors normal-case tracking-normal text-xs"
                  title="Cerca nuove foto su Brave (bypass cache)"
                  data-testid="photo-search-refresh-pool"
                >
                  <RefreshCw size={11} /> Nuove foto
                </button>
              </div>
              {query && (
                <div className="mt-2 text-[10px] tracking-[0.15em] uppercase text-slate1/60">
                  Query: <span className="text-ink/80">{query}</span>
                </div>
              )}
            </>
          )}

          {/* Vista ingrandita */}
          {!loading && selected && (
            <div className="flex flex-col items-center" data-testid="photo-search-expanded">
              {/* Toolbar nav */}
              <div className="w-full flex items-center justify-between mb-3 text-[11px] tracking-[0.15em] uppercase text-slate1">
                <button
                  onClick={() => setSelectedIdx(-1)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/15 rounded-sm hover:bg-ink/5"
                  data-testid="photo-search-back-grid"
                >
                  <ChevronLeft size={12} /> Torna alla griglia
                </button>
                <span>{selectedIdx + 1} / {items.length}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedIdx((i) => Math.max(0, i - 1))}
                    disabled={selectedIdx === 0}
                    className="p-1.5 border border-ink/15 rounded-sm hover:bg-ink/5 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Precedente"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={() => setSelectedIdx((i) => Math.min(items.length - 1, i + 1))}
                    disabled={selectedIdx === items.length - 1}
                    className="p-1.5 border border-ink/15 rounded-sm hover:bg-ink/5 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Successiva"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
              {/* Image */}
              <div className="w-full bg-white border border-ink/10 rounded-sm overflow-hidden flex items-center justify-center" style={{ maxHeight: "60vh", minHeight: "300px" }}>
                <img
                  src={selected.url}
                  alt={selected.title || ""}
                  className="max-h-[60vh] max-w-full object-contain"
                  data-testid="photo-search-full-image"
                  onError={(e) => {
                    // Fallback su thumbnail in caso l'immagine full sia bloccata da hotlink protection
                    if (e.target.src !== selected.thumbnail) {
                      e.target.src = selected.thumbnail;
                    }
                  }}
                />
              </div>
              {/* Info */}
              <div className="w-full mt-4 bg-white border border-ink/10 rounded-sm p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-ink leading-snug">{selected.title || "Senza titolo"}</div>
                    <div className="mt-1 text-[11px] text-slate1 flex items-center gap-3 flex-wrap">
                      {selected.width && selected.height && (
                        <span>{selected.width}×{selected.height}px</span>
                      )}
                      <span>· Clarity {selected.clarity_score}/100 · Color {selected.color_score}/100</span>
                    </div>
                    {selected.reason && (
                      <div className="mt-2 text-[11px] italic text-slate1 leading-relaxed">
                        <span className="text-gold not-italic">↳</span> {selected.reason}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canReject && (
                    <button
                      onClick={() => rejectPhoto(selected.url)}
                      disabled={rejectingUrl === selected.url}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-rose-200 text-[11px] tracking-[0.2em] uppercase text-rose-700 hover:bg-rose-50 rounded-sm disabled:opacity-50"
                      data-testid="photo-search-expanded-reject"
                      title="Scarta questa foto · cerco un sostituto"
                    >
                      {rejectingUrl === selected.url ? <Loader2 size={12} className="animate-spin" /> : <ThumbsDown size={12} />}
                      Scarta
                    </button>
                    )}
                    <button
                      onClick={() => downloadPhoto(selected)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90"
                      data-testid="photo-search-download"
                    >
                      <Download size={12} /> Scarica
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer (solo griglia) */}
        {!loading && selectedIdx < 0 && items.length > 0 && (
          <div className="px-6 py-3 border-t border-ink/10 bg-white flex items-center justify-between text-[10px] tracking-[0.15em] uppercase text-slate1">
            <span>Cache TTL 30gg · {items.length} risultati ranked</span>
            <span className="flex items-center gap-1.5">
              <ImageIcon size={11} /> Click sulla miniatura per ingrandire e scaricare
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
